import { useMutation, useQuery } from "@tanstack/react-query"
import { stringify } from "querystringify"

import { CALL_REFETCH_DELAY, CALL_PAGE } from "modules/Call/common/constants"
import { AxiosError, httpClient } from "utils/infrastructure/httpClient"

export type CallStatus =
    | "NEW"
    | "UPCOMING"
    | "CURRENT"
    | "STARTED"
    | "ENDED"
    | "FEEDBACK_RECEIVED"
    | "PASSED"

export interface UpcomingCallBooking {
    booking_hash: string
    session_start_datetime: string
}

export interface UpcomingCall {
    id: number
    status: CallStatus
    booking: UpcomingCallBooking
}

export interface CallBookingClient {
    id: number
    name: string
    phone: string
    email: string
    has_questionnaire_answers: boolean
}

export interface CallBookingPsychologist {
    hash_id: string
    display_name: string
    face_url: string
    titles: string[]
    private_number: string
    video_call_handle: string
}

export interface CallBooking {
    id: number
    booking_hash: string
    online: boolean
    session_start_datetime: string
    session_duration: number
    client: CallBookingClient
    psychologist: CallBookingPsychologist
}

export interface Call {
    id: number
    status: CallStatus
    minutes_before_start: number
    booking: CallBooking
    companion_status: CALL_PAGE
}

export interface UpcomingBookingClient {
    id: number
    name: string
    email: string
    has_questionnaire_answers: boolean
}

export interface SimplifiedCall {
    id: number
    status: CallStatus
}

export interface UpcomingBooking {
    booking_hash: string
    session_start_datetime: string
    client: UpcomingBookingClient
    online: boolean
    service_type: string
    call?: SimplifiedCall
    alter_video_call: boolean
    is_booking_available_for_call: boolean
}

export interface InternalCall {
    id: number
    name: string
    voximplant_user_name: string
}

export const GET_CALL_QUERY_KEY = "get-call"
export const GET_PSYCHOLOGIST_UPCOMING_BOOKING_QUERY_KEY = "get-psychologist-upcoming-booking"

function getClientUpcomingCall() {
    return httpClient.get<UpcomingCall>("call/v2/client/upcoming")
}

export function useGetUpcomingCall(enabled: boolean) {
    return useQuery([], getClientUpcomingCall, {
        refetchOnWindowFocus: true,
        refetchInterval: CALL_REFETCH_DELAY,
        enabled,
        notifyOnChangeProps: ["dataUpdatedAt"],
    })
}

function getClientCall(bookingHash: string, callPage: CALL_PAGE) {
    return httpClient.get<Call>(
        `call/v2/client/${bookingHash}?${stringify({ call_page: callPage })}`
    )
}

export function useGetClientCall(bookingHash: string, callPage: CALL_PAGE, isRefetch: boolean) {
    return useQuery([GET_CALL_QUERY_KEY, bookingHash], () => getClientCall(bookingHash, callPage), {
        refetchOnWindowFocus: false,
        refetchInterval: isRefetch ? CALL_REFETCH_DELAY : undefined,
        onError: (e: AxiosError) => e,
    })
}

export function clientChangeCallStatus(bookingHash: string, status: CallStatus) {
    return httpClient.post<Call>(`call/v2/client/${bookingHash}/change-status`, {
        status,
    })
}

export function useClientChangeCallStatus() {
    return useMutation(({ bookingHash, status }: { bookingHash: string; status: CallStatus }) =>
        clientChangeCallStatus(bookingHash, status)
    )
}

function getPsyCall(bookingHash: string, callPage: CALL_PAGE) {
    return httpClient.get<Call>(
        `call/v2/psychologist/${bookingHash}?${stringify({ call_page: callPage })}`
    )
}

export function useGetPsyCall(bookingHash: string, callPage: CALL_PAGE, isRefetch: boolean) {
    return useQuery([GET_CALL_QUERY_KEY, bookingHash], () => getPsyCall(bookingHash, callPage), {
        refetchOnWindowFocus: false,
        refetchInterval: isRefetch ? CALL_REFETCH_DELAY : undefined,
        onError: (e: AxiosError) => e,
    })
}

export function getPsyUpcomingBooking() {
    return httpClient.get<UpcomingBooking>("call/v2/psychologist/upcoming-booking")
}

export function useGetPsyUpcomingBooking() {
    return useQuery<UpcomingBooking>(
        [GET_PSYCHOLOGIST_UPCOMING_BOOKING_QUERY_KEY],
        getPsyUpcomingBooking,
        {
            refetchOnWindowFocus: false,
            refetchInterval: CALL_REFETCH_DELAY,
        }
    )
}

export function psyChangeCallStatus(bookingHash: string, status: CallStatus) {
    return httpClient.post<Call>(`call/v2/psychologist/${bookingHash}/change-status`, {
        status,
    })
}

export function usePsyChangeCallStatus() {
    return useMutation(({ bookingHash, status }: { bookingHash: string; status: CallStatus }) =>
        psyChangeCallStatus(bookingHash, status)
    )
}

function getInternalCall() {
    return httpClient.get<InternalCall>("call/internal/auth")
}

export function useGetInternalCall() {
    return useQuery<InternalCall>([GET_CALL_QUERY_KEY], getInternalCall, {
        refetchOnWindowFocus: false,
    })
}
